import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import { Form, Formik } from 'formik';
import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import {
  updateRegistrationData,
  updateRegistrationStep,
  verifySocialSecurityNumber,
  updateRegistrationAlert
} from 'store/register/actions';
import { registerSubmittingSelector } from 'store/register/selectors';
import { RegisterData, RegisterAlert } from 'store/register/types';
import * as Yup from 'yup';
import { Step, formatAlert } from 'store/pageActions/RegisterAccount/constants';
import { CancelButton } from '../FormElements/CancelButton';
import CustomButton from '../FormElements/CustomButton';
import CustomTextField from '../FormElements/CustomTextField';
import analyticsService, { AnalyticsEvent } from 'services/AnalyticsService';

interface Props {
  isSubmitting: boolean;
  updateRegister: (data: Partial<RegisterData>) => void;
  updateAlert: (data: RegisterAlert) => void;
  verifySocial: () => Promise<AnyAction>;
  updateStep: (step: string) => void;
}

const VerifySchema = Yup.object().shape({
  ssn: Yup.string()
    .required('Required')
    .min(9, 'Must be 9 digits')
    .max(11, 'Must be 9 digits')
    .matches(/^\d{3}-\d{2}-\d{4}$/, 'The Social Security number is 9 digits')
});

const RegisterVerifySocial = ({
  isSubmitting,
  updateAlert,
  updateRegister,
  verifySocial,
  updateStep
}: Props) => {
  useEffect(() => {
    updateAlert(
      formatAlert(
        'info',
        'Verify Identity',
        `We are having trouble identifying you. To ensure we have the correct information,
      please provide your social security number.`
      )
    );
  }, []);

  useEffect(() => {
    analyticsService.logEvent(AnalyticsEvent.AccountCreationSSNRequested);
  }, []);

  const handleSubmit = (values: Partial<RegisterData>) => {
    const ssn = values.ssn?.replace(/-/g, '');
    updateRegister({ ssn });
    verifySocial().then(res => {
      updateAlert(formatAlert());
      if (res.error) {
        updateStep(Step.ERROR);
      } else {
        updateStep(Step.ACCOUNT);
      }
    });
  };

  return (
    <Formik
      initialValues={{
        ssn: ''
      }}
      validationSchema={VerifySchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, dirty }) => {
        return (
          <Form>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <CustomTextField
                  name="ssn"
                  mask="ssn"
                  label="Social Security Number"
                  placeholder="000-00-0000"
                />
              </Grid>
            </Grid>
            <Box my={3} maxWidth="xs">
              <CustomButton
                type="submit"
                label="Continue"
                disabled={!(isValid && dirty)}
                isSubmitting={isSubmitting}
                aria-label="submit-button"
              />
              <CancelButton />
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

const mapStateToProps = (state: RootState) => ({
  isSubmitting: registerSubmittingSelector(state)
});

const mapDispatchToProps = {
  updateRegister: updateRegistrationData,
  verifySocial: verifySocialSecurityNumber,
  updateStep: updateRegistrationStep,
  updateAlert: updateRegistrationAlert
};

export default connect(mapStateToProps, mapDispatchToProps)(RegisterVerifySocial);
